import type { PollAssignment, VmPollOverlay } from 'Video/components/vm-poll-overlay/vm-poll-overlay';

/**
 * Polls Controller
 *
 * Thin coordination layer between the Brightcove player and the vm-poll-overlay
 * component. Listens to player timeline events and triggers polls.
 *
 * A poll only shows when the user naturally watches through its trigger point.
 * Seeking past or into a poll's window does not trigger it.
 *
 * Seek detection uses time-jump comparison rather than seeking/seeked events,
 * because browsers fire timeupdate with the new position before seeked fires.
 * See: https://github.com/videojs/video.js/issues/3446
 */

// timeupdate fires ~4x/sec during playback. Normal tick delta is ~0.25s.
// A jump larger than this threshold indicates a seek.
const SEEK_THRESHOLD_SECONDS = 2;

export class PollsController {
    private _overlay: VmPollOverlay;
    private _assignments: PollAssignment[];
    private _player: any;
    private _lastTime: number = -1;
    private _pausedByPoll: boolean = false;
    private _originalParent: ParentNode | null = null;
    private _originalNextSibling: Node | null = null;

    constructor(overlay: VmPollOverlay, player: any, assignments: PollAssignment[]) {
        this._overlay = overlay;
        this._assignments = assignments;
        this._player = player;

        overlay.assignments = assignments;
        player.on('timeupdate', () => this._onTimeUpdate());

        overlay.addEventListener('poll-hover', () => this._onPollHover());
        overlay.addEventListener('poll-hidden', () => this._onPollHidden());

        player.on('fullscreenchange', () => this._onFullscreenChange());
    }

    private _onPollHover() {
        if (this._pausedByPoll) return;
        if (this._player.paused() || this._player.ended()) return;
        this._player.pause();
        this._pausedByPoll = true;
    }

    private _onPollHidden() {
        if (!this._pausedByPoll) return;
        this._pausedByPoll = false;
        this._player.play();
    }

    /**
     * Mobile browsers (notably iOS Safari) put the bare <video> element into
     * native HTML5 fullscreen instead of fullscreening the player wrapper.
     * In that mode, no DOM overlay can render on top, so the desktop reparent
     * trick can't help and we should suppress the trigger instead of pretending
     * the poll was shown.
     */
    private _isNativeFullscreen(): boolean {
        if (!this._player.isFullscreen()) return false;
        const playerEl: HTMLElement | undefined = this._player.el?.();
        return document.fullscreenElement !== playerEl;
    }

    /**
     * The user switched to another tab or window while the video kept playing
     * in the background. Counting this as an impression inflates poll views for
     * a poll the user never actually saw, so we suppress the trigger and record
     * it separately instead.
     */
    private _isBackgrounded(): boolean {
        return document.visibilityState === 'hidden';
    }

    /**
     * Re-parent the overlay into the player element when fullscreen is entered so
     * polls remain visible above the fullscreened video. Restore the original
     * location on exit. Brightcove fullscreens the player element only — anything
     * outside it (including the overlay's normal parent) is hidden by the
     * fullscreen layer.
     */
    private _onFullscreenChange() {
        const playerEl: HTMLElement | undefined = this._player.el?.();
        if (!playerEl) return;

        if (this._player.isFullscreen()) {
            this._originalParent = this._overlay.parentNode;
            this._originalNextSibling = this._overlay.nextSibling;
            playerEl.appendChild(this._overlay);
        } else if (this._originalParent) {
            this._originalParent.insertBefore(this._overlay, this._originalNextSibling);
            this._originalParent = null;
            this._originalNextSibling = null;
        }
    }

    private _onTimeUpdate() {
        const currentTime = this._player.currentTime();
        const delta = currentTime - this._lastTime;

        // First tick or seek detected — update position without triggering or
        // feeding the jump into the overlay's auto-dismiss countdown.
        if (this._lastTime < 0 || Math.abs(delta) > SEEK_THRESHOLD_SECONDS) {
            this._lastTime = currentTime;
            return;
        }

        // Feed elapsed *playback* time to the overlay, which owns the countdown
        // and dismisses itself once a poll's duration elapses. timeupdate only
        // fires while the video plays, so pausing (e.g. the hover-to-pause)
        // freezes the countdown, matching Brightcove's polls. No-op when no
        // poll is active.
        this._overlay.advancePlayback(delta);

        for (const assignment of this._assignments) {
            if (this._overlay.hasBeenShown(assignment.id)) continue;

            const trigger = assignment.trigger_seconds;

            if (this._lastTime < trigger && currentTime >= trigger) {
                if (this._isNativeFullscreen()) {
                    this._overlay.suppressPoll(assignment.id, 'fullscreen');
                } else if (this._isBackgrounded()) {
                    this._overlay.suppressPoll(assignment.id, 'background');
                } else {
                    this._overlay.showPoll(assignment.id);
                }
                break;
            }
        }

        this._lastTime = currentTime;
    }
}
